// Interfaces
import { IUnreadCount, ITrashedUnreadCount } from '../interfaces/message.interfaces';
import { virtualFoldersEnum, IFolder } from '../interfaces/folder.interfaces';
import { FolderTypeNames } from '../enums';

export function getUnreadCountByFolderId(
    folderId: string,
    unreadCounter: IUnreadCount,
    trashUnreadCounter: ITrashedUnreadCount
): number {
    if (unreadCounter) {
        if (folderId === virtualFoldersEnum.DELETE_FOLDER) {
            return trashUnreadCounter ? trashUnreadCounter.trashedUnreadCount : 0;
        }

        if (folderId === virtualFoldersEnum.UNREAD_FOLDER) {
            return unreadCounter ? unreadCounter.unreadCount : 0;
        }

        const item = unreadCounter.items.find((item) => {
            return item.folderId === folderId;
        });
        if (item) {
            return item._count.id;
        }
    }

    return 0;
}

export const getSystemFolderTranslateKey = (folder: IFolder): string => {
    if (!folder) {
        return '';
    }

    switch (folder.folderType) {
        case FolderTypeNames.ROOT:
            return 'FOLDER_CHOOSE';

        case FolderTypeNames.DRAFT:
            return 'DRAFTS';

        case FolderTypeNames.INBOX:
            return 'INBOX';

        case FolderTypeNames.SENT:
            return 'SENT_ITEMS';

        case FolderTypeNames.UNREAD:
            return 'UNREAD_MAIL';

        case FolderTypeNames.TRASH:
            return 'DELETED_ITEMS';

        case FolderTypeNames.MORE:
            return 'MORE_FOLDERS';

        default:
            return folder.name;
    }
};

export const getFolderName = (folder: IFolder, formatMessage: ({ id }: { id: string }) => string): string => {
    if (!folder) {
        return '';
    }

    switch (folder.folderType) {
        case FolderTypeNames.ROOT:
            return formatMessage({ id: 'FOLDER_CHOOSE' });

        case FolderTypeNames.DRAFT:
            return formatMessage({ id: 'DRAFTS' });

        case FolderTypeNames.INBOX:
            return formatMessage({ id: 'INBOX' });

        case FolderTypeNames.SENT:
            return formatMessage({ id: 'SENT_ITEMS' });

        case FolderTypeNames.UNREAD:
            return formatMessage({ id: 'UNREAD_MAIL' });

        case FolderTypeNames.FLAGGEDFOLDER:
            return formatMessage({ id: 'FLAGFOLDER' });

        case FolderTypeNames.TRASH:
            return formatMessage({ id: 'DELETED_ITEMS' });

        case FolderTypeNames.MORE:
            return formatMessage({ id: 'MORE_FOLDERS' });

        default:
            return folder.name;
    }
};

export const getRootFolderFromFolderList = (folderList: IFolder[]): IFolder => {
    // get root folder, there can only be 1
    return folderList.find((value: IFolder) => {
        return value.parentId === null;
    });
};

export const getFolderFromFolderList = (folderList: IFolder[]): IFolder => {
    // get root folder, there can only be 1
    return folderList.find((value: IFolder) => {
        return value.parentId === null;
    });
};

export const getFolderById = (id: string, folderList: IFolder[]): IFolder => {
    return folderList.find((folder) => {
        return folder.id === id;
    });
};

export const isSentFolder = (folderId: string, folderList: IFolder[]): boolean => {
    const folder = getFolderById(folderId, folderList);
    if (folder) {
        return folder.folderType === FolderTypeNames.SENT;
    }
    return false;
};

export const isDraftFolder = (folderId: string, folderList: IFolder[]): boolean => {
    const folder = getFolderById(folderId, folderList);
    if (folder) {
        return folder.folderType === FolderTypeNames.DRAFT;
    }
    return false;
};

export const isSystemFolder = (folderId: string, folderList: IFolder[]): boolean => {
    const folder = getFolderById(folderId, folderList);
    if (folder) {
        return [
            FolderTypeNames.INBOX,
            FolderTypeNames.DRAFT,
            FolderTypeNames.SENT,
            FolderTypeNames.TRASH,
            FolderTypeNames.MORE,
            FolderTypeNames.UNREAD,
            FolderTypeNames.FLAGGEDFOLDER,
        ].includes(folder.folderType as FolderTypeNames);
    }
    return false;
};

export const isVirtualFolder = (folderId: virtualFoldersEnum): boolean => {
    return Object.values(virtualFoldersEnum).includes(folderId);
};

export const getVirtualFolders = (
    mailboxId: string,
    rootFolderId: string,
    returnFolders: Array<virtualFoldersEnum> = null
): IFolder[] => {
    const virtualFolders: IFolder[] = [];

    virtualFolders.push({
        name: 'More folders',
        id: virtualFoldersEnum.MORE_FOLDERS,
        parentId: rootFolderId,
        folderType: FolderTypeNames.MORE,
        isActive: true,
        mailboxId: mailboxId,
        version: 0,
    });

    virtualFolders.push({
        name: 'Unread mails',
        id: virtualFoldersEnum.UNREAD_FOLDER,
        parentId: rootFolderId,
        folderType: FolderTypeNames.UNREAD,
        isActive: true,
        mailboxId: mailboxId,
        version: 0,
    });

    virtualFolders.push({
        name: 'Deleted mails',
        id: virtualFoldersEnum.DELETE_FOLDER,
        parentId: rootFolderId,
        folderType: FolderTypeNames.TRASH,
        isActive: true,
        mailboxId: mailboxId,
        version: 0,
    });

    if (returnFolders && returnFolders.length > 0) {
        const virtualFoldersToReturn: IFolder[] = [];
        returnFolders.forEach((returnFolderEnum: virtualFoldersEnum) => {
            const virtualFolder = virtualFolders.find((folder: IFolder) => {
                return folder.id === returnFolderEnum;
            });
            if (virtualFolder) {
                virtualFoldersToReturn.push(virtualFolder);
            }
        });
        return virtualFoldersToReturn;
    }

    return virtualFolders;
};

export const removeRelatedFolders = (fromFolder: IFolder, folders: IFolder[]): IFolder[] => {
    const idsToRemove = findChildren(fromFolder.id, folders);
    return folders.filter((folder) => !idsToRemove.includes(folder.id));
};

export const findChildren = (parentId: string, folders: IFolder[]): string[] => {
    const children: IFolder[] = folders.filter((folder) => folder.parentId === parentId);
    if (children.length < 1) {
        return [parentId];
    }

    return children.reduce(
        (idList: string[], folder) => {
            return [...idList, ...findChildren(folder.id, folders)];
        },
        [parentId]
    );
};
